import {Observable} from 'rxjs';
import {List} from 'immutable';

import BillPayer from 'web-app/react/entities/bill-payers/model';
import AccountItem from 'web-app/react/entities/account-items/model';
import * as AccountItemConstants from 'web-app/react/entities/account-items/constants';
import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import LocalStorageService from 'web-app/services/local-storage';
import Table from 'web-app/react/components/table/model';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {getApolloClient} from 'web-app/api/clients/famly-app';

import * as Queries from './queries';

export const fetchBalance = (
    institutionId,
    balanceId,
    filterCreditedInvoices = false,
    onlyPreviousBillPayers = false,
    limit,
    expectedIds,
    unexpectedIds,
) => {
    return RESTClient.getAuthenticatedObservable(`v2/invoicing/${institutionId}/${balanceId}/balance`, {
        onlyPreviousBillPayers,
        filterCreditedInvoices,
        limit,
        expectedIds,
        unexpectedIds,
    });
};

export const fetchPaymentsForTarget = (
    targetId,
    offset,
    limit,
    from,
    to,
    confirmedFilter = AccountItemConstants.CONFIRMED_FILTER_ALL,
    paymentMethod,
    deposited,
    transactionStatus,
    createdFrom,
    createdTo,
    includeDeleted,
    billPayerIds,
    sortBy,
    sortAscending,
) => {
    return RESTClient.getAuthenticatedObservable('v2/invoicing/payments', {
        target: targetId,
        offset,
        limit,
        from,
        to,
        paymentMethod,
        deposited,
        transactionStatus,
        includeConfirmed: Boolean(
            confirmedFilter === AccountItemConstants.CONFIRMED_FILTER_ALL ||
                confirmedFilter === AccountItemConstants.CONFIRMED_FILTER_CONFIRMED,
        ),
        includeUnconfirmed: Boolean(
            confirmedFilter === AccountItemConstants.CONFIRMED_FILTER_ALL ||
                confirmedFilter === AccountItemConstants.CONFIRMED_FILTER_UNCONFIRMED,
        ),
        createdFrom,
        createdTo,
        includeDeleted,
        billPayerIds,
        sortBy,
        sortAscending,
    }).map(data => ({
        csv: data.csv,
        payments: data.payments.map(AccountItem.fromAPI),
        invoices: Object.values(data.invoices).map(AccountItem.fromAPI),
        billPayers: data.billPayers.map(BillPayer.fromAPI),
        total: data.total,
    }));
};

export const fetchInvoicesForTarget = (targetId, offset, limit, from, to, createdFrom, createdTo, billPayerIds) => {
    return RESTClient.getAuthenticatedObservable('v2/invoicing/invoices', {
        target: targetId,
        offset,
        limit,
        from,
        to,
        createdFrom,
        createdTo,
        billPayerIds,
    }).map(data => ({
        csv: data.csv,
        behaviors: new BehaviorsCollection(data.behaviors),
        invoices: data.invoices.map(AccountItem.fromAPI),
        billPayers: data.billPayers.map(BillPayer.fromAPI),
    }));
};

export const createPayment = (institutionId, billPayerId, payment) => {
    return RESTClient.postAuthenticatedObservable(`v2/invoicing/${institutionId}/${billPayerId}/payments`, payment);
};

export const updatePayment = (institutionId, billPayerId, paymentId, payment) => {
    return RESTClient.putAuthenticatedObservable(
        `v2/invoicing/${institutionId}/${billPayerId}/payments/${paymentId}`,
        payment,
    );
};

export const deletePayment = (institutionId, billPayerId, paymentId) => {
    return RESTClient.deleteAuthenticatedObservable(
        `v2/invoicing/${institutionId}/${billPayerId}/payments/${paymentId}`,
    );
};

export const movePayment = (institutionId, billPayerId, paymentId, newBillPayerId) => {
    return RESTClient.postAuthenticatedObservable(
        `v2/invoicing/${institutionId}/${billPayerId}/payments/${paymentId}/move`,
        {
            newBillPayerId,
        },
    );
};

const toInvoicesWithRuns = result => ({
    invoices: List(result.data.finance.invoices.create.invoices).map(AccountItem.fromGraphQLInvoice).filter(hasValue),
    runIds: result?.data?.finance?.invoices?.create?.runs?.map(run => run.runId) ?? [],
});

export const createInvoiceInApollo = variables =>
    Observable.fromPromise(
        getApolloClient().mutate({
            mutation: Queries.CreateInvoice,
            variables,
        }),
    ).map(toInvoicesWithRuns);

export const persistBatchInvoiceWithoutPDF = variables =>
    Observable.fromPromise(
        getApolloClient().mutate({
            mutation: Queries.PersistBatchInvoiceWithoutPDF,
            variables,
        }),
    ).map(toInvoicesWithRuns);

export const previewBatchInvoice = variables =>
    Observable.fromPromise(
        getApolloClient().mutate({
            mutation: Queries.PreviewBatchInvoiceWithoutPDF,
            variables,
        }),
    ).map(toInvoicesWithRuns);

export const createInvoice = (institutionId, billPayerId, invoice, options) => {
    const reInvoice = Boolean(LocalStorageService.getFromLocalStorage('reInvoice')) && Boolean(invoice.preview);

    return RESTClient.postAuthenticatedObservable(
        `v2/invoicing/${institutionId}/${billPayerId}/invoices`,
        {
            ...invoice,
            reInvoice,
        },
        options,
    );
};

export const previewInvoice = (institutionId, billPayerId, invoice, options) =>
    createInvoice(
        institutionId,
        billPayerId,
        {
            ...invoice,
            preview: true,
        },
        options,
    ).map(result => ({
        details: List(result.details.children).map(({child, tables}) => ({
            child,
            tables: List(tables).map(table => Table.fromAPI(table)),
        })),
        invoice: result.invoice ? AccountItem.fromAPI(result.invoice) : null,
        warnings: result.warnings,
    }));
